import React from "react";
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Text,
} from "@chakra-ui/react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import type { PineExecutionModel } from "../../types/reportStrategyPine";
import { STRATEGY_PINE_SECTION_DESC_CLASS, STRATEGY_PINE_SECTION_LABEL_CLASS } from "./StrategyPineSectionCard";
import { STRATEGY_PINE_ALERT_CLASS, StrategyPineAlertIcon } from "./strategyPineAlertUi";

export type StrategyPineExecutionModelCalloutProps = {
  executionModel?: PineExecutionModel | null;
  subtleText: string;
  textColor: string;
  borderColor: string;
};

export function StrategyPineExecutionModelCallout({
  executionModel,
  subtleText,
  textColor,
  borderColor,
}: StrategyPineExecutionModelCalloutProps): React.ReactElement | null {
  if (!executionModel) return null;

  const warnings = executionModel.warnings ?? [];
  const flags = executionModel.strategy_flags;
  const hasStrategyFlags =
    flags != null &&
    typeof flags === "object" &&
    !Array.isArray(flags) &&
    Object.keys(flags).length > 0;

  if (warnings.length === 0 && !hasStrategyFlags) return null;

  return (
    <Box>
      {warnings.length > 0 ? (
        <Alert variant="info" className={`mb-2 ${STRATEGY_PINE_ALERT_CLASS}`}>
          <StrategyPineAlertIcon tone="info" />
          <AlertTitle className={STRATEGY_PINE_SECTION_LABEL_CLASS}>Execution model</AlertTitle>
          <AlertDescription className={STRATEGY_PINE_SECTION_DESC_CLASS}>
            <p className="mb-2">
              Tessera&apos;s simulator uses one pass per bar when applying policy; your script may
              bar-close or reorder differently on TradingView.
            </p>
            <ul className="list-disc space-y-1 pl-4">
              {warnings.map((w, i) => (
                <li key={i}>{w}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      ) : null}

      {hasStrategyFlags ? (
        <Accordion allowToggle borderWidth="1px" borderColor={borderColor} borderRadius="8px">
          <AccordionItem border="none">
            <AccordionButton px={3} py={2} _hover={{ bg: "transparent" }}>
              <Box flex="1" textAlign="left">
                <Text fontSize="sm" fontWeight="medium" color={textColor}>
                  Advanced strategy flags
                </Text>
              </Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel px={3} pb={3} pt={0}>
              <Text fontSize="xs" fontFamily="mono" color={subtleText} whiteSpace="pre-wrap">
                {JSON.stringify(flags, null, 2)}
              </Text>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      ) : null}
    </Box>
  );
}
